/**
 * @file src/sync/personalTrackSync.js
 * @description The viewer's own track sync — where it came from, and what that means for a save.
 *
 * A track carries two versions of the same settings. `metadata.effectiveSettings.sync` is the
 * RESOLVED answer the server worked out for this viewer (their own value, else the owner's, else
 * what the audio analysis found), and `trackUserSettings.sync` is the viewer's own layer on its
 * own. The resolvers read the resolved one first, because it is the already-decided answer.
 *
 * That is correct as long as both were minted together. Two situations break it, and both end in
 * lost settings, because the backend replaces a track's stored sync WHOLESALE — every commit
 * re-sends warp, meter and the loop for exactly that reason.
 *
 * ## A downloaded collection resolves from a frozen answer
 *
 * A stored manifest wins even with a connection, by design. So the release — and the resolved layer
 * inside it — is the one captured at download time, while the personal layer is fetched fresh on
 * every boot. Change a loop on another device, open the downloaded collection here, and the fresh
 * loop is fetched and then ignored by the resolvers. Edit the tempo, and the commit re-sends the
 * frozen loop over the new one.
 *
 * So a personal layer READ FROM THE SERVER is folded over the resolved one. It is the newer of two
 * accounts of the same thing, and folding it is the same patch `trackUserSettingsPersistence` applies
 * after a save — which is why {@link foldPersonalSyncOverResolved} is the single rule both use.
 *
 * And the fold is written back to the per-owner store, so a boot with no network gets it too. That
 * row is otherwise minted only by a download, which leaves a collection downloaded before the split
 * with no row of its own — see {@link mirrorFoldedEffectiveSettings}.
 *
 * A fold has to be able to say "this is gone", not only "this is different". Deleting your personal
 * settings on another device produces a fresh layer that STATES NOTHING, and a fold that only
 * overlays what it finds would leave every deleted value running. So the rule is symmetric: a field
 * the resolved answer credits to this person, and the fresh layer no longer claims, was deleted by
 * them and is dropped.
 *
 * ## A read that failed looks exactly like a person who never chose anything
 *
 * `null` was the answer for a 404, a 401, a timeout and an unreachable server alike. The engine then
 * auto-detects, and the next unrelated commit writes those detected values over real saved settings.
 * An offline session is protected by the write door; a session that boots ONLINE with a settings GET
 * that merely fails is not. So the outcome travels with the payload, and a wholesale write is
 * refused unless the layer it would overwrite was actually read.
 */
import { Constants } from '../config/Constants.js';
import { CONFIGURATION_KINDS, READ_OUTCOME, mirrorAccepted } from '../config/configurationPack.js';
import { readStoredRelease, releaseRecordKey } from '../offline/offlineLibraryStore.js';
import { isLocalEntryId } from '../local/localAudioIds.js';

/** What a `*Source` marker says when the value is this person's own. */
const USER = 'user';

/**
 * The fields a personal layer can state, keyed the way the RESOLVED layer keys them, each beside the
 * marker naming whose value it is. The loop REGION is deliberately absent: it is a pair under one
 * marker, handled on its own below.
 */
const SYNC_FIELD_MARKERS = Object.freeze({
  trackBpm: 'trackBpmSource',
  primaryGridMarkerTimeSec: 'primaryGridMarkerTimeSecSource',
  meter: 'meterSource',
  warpEnabled: 'warpEnabledSource',
  loopEnabled: 'loopEnabledSource',
});

/** Where the resolved layer lives on a combined config, in the shapes the resolvers accept. */
function resolvedSyncOf(combined) {
  const sync = combined?.track?.metadata?.effectiveSettings?.sync;
  return sync && typeof sync === 'object' ? sync : null;
}

/**
 * The personal layer's own reading of each field the commit path re-sends, keyed the way the
 * RESOLVED layer keys it.
 *
 * The two shapes differ in one place: the personal layer stores the downbeat as a grid-marker
 * record, the resolved layer as a plain `primaryGridMarkerTimeSec`. Everything else is the same
 * name on both sides.
 */
function personalSyncAsResolved(personalSync) {
  const stated = {};
  if (!personalSync || typeof personalSync !== 'object') return stated;

  if (Number.isFinite(Number(personalSync.trackBpm)) && Number(personalSync.trackBpm) > 0) {
    stated.trackBpm = Number(personalSync.trackBpm);
  }
  const markerTime = personalSync.primaryGridMarkerTimeSec
    ?? personalSync.gridMarkers?.[0]?.sourceTimeSec
    ?? personalSync.gridStartTimeSec;
  if (Number.isFinite(Number(markerTime)) && Number(markerTime) >= 0) {
    stated.primaryGridMarkerTimeSec = Number(markerTime);
  }
  if (typeof personalSync.meter === 'string' && personalSync.meter) {
    stated.meter = personalSync.meter;
  }
  // Booleans get a type test, never a truthiness one: warp-off and loop-off are choices, and `||`
  // would collapse them into "nobody said".
  if (typeof personalSync.warpEnabled === 'boolean') stated.warpEnabled = personalSync.warpEnabled;
  if (typeof personalSync.loopEnabled === 'boolean') stated.loopEnabled = personalSync.loopEnabled;

  // The loop REGION travels as a pair or not at all — half a region is a broken loop, not half of
  // one, and the two edges have to move together or the fold can leave a start from one device
  // beside an end from another.
  const start = Number(personalSync.loopStartSec);
  const end = Number(personalSync.loopEndSec);
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    stated.loopStartSec = start;
    stated.loopEndSec = end;
  }

  return stated;
}

/**
 * THE fold rule, in one place: a resolved sync block, brought into line with what this person's own
 * layer currently says.
 *
 * Both callers of it are looking at the same situation from different ends. On boot the personal
 * layer has just been read from the server; after a save it is the payload the server has just
 * accepted — and because a save REPLACES the stored block rather than merging into it, the payload
 * is the whole of the person's layer either way. So one rule serves both, and the two cannot drift:
 *
 * - a field the layer states becomes the resolved value, marked as this person's
 * - a field the layer does NOT state, but the resolved block credits to this person, was deleted by
 *   them — on this device or another — and goes, along with its marker, so the resolvers fall back
 *   to the owner's value or to detection instead of serving something that no longer exists
 * - a field credited to the OWNER is left alone: this person never overrode it, so they cannot have
 *   deleted it either
 *
 * The loop is the case that made this necessary. A cleared loop is expressed by OMITTING the region
 * pair while still stating `loopEnabled`, so a fold that only overlaid what it found flipped the
 * flag and left the region — and the next unrelated wholesale save re-sent it, resurrecting a loop
 * the person had deleted.
 *
 * KNOWN LIMITATION, and not a fixable one: a deletion is only detected where the resolved block
 * SAYS whose value it is. A payload minted before the `*Source` markers existed carries none, so
 * every field the fresh layer omits is read as "the owner's, leave it alone" and survives — a loop
 * deleted elsewhere goes on running against an old payload until something re-mints it. Provenance
 * that was never recorded cannot be guessed, and guessing it the other way (treat unmarked as
 * this person's) would delete the OWNER's settings on every fold, which is the worse mistake.
 *
 * @param {object} resolvedSync the resolved block (never mutated)
 * @param {object|null} personalSync this person's own layer, in its own key shape
 * @returns {object} a new resolved block
 */
export function foldPersonalSyncOverResolved(resolvedSync, personalSync) {
  const stated = personalSyncAsResolved(personalSync);
  const folded = { ...resolvedSync };

  for (const [field, marker] of Object.entries(SYNC_FIELD_MARKERS)) {
    if (field in stated || folded[marker] !== USER) continue;
    delete folded[field];
    delete folded[marker];
  }
  if (!('loopStartSec' in stated) && folded.loopRangeSource === USER) {
    delete folded.loopStartSec;
    delete folded.loopEndSec;
    delete folded.loopRangeSource;
  }

  Object.assign(folded, stated);
  for (const field of Object.keys(stated)) {
    // The `*Source` markers say which layer produced each value. Folding without moving them would
    // leave a value marked as the owner's while holding this person's.
    if (field === 'loopStartSec' || field === 'loopEndSec') folded.loopRangeSource = USER;
    else folded[SYNC_FIELD_MARKERS[field]] = USER;
  }

  return folded;
}

/**
 * What a personal-layer read found, per CONFIG rather than per track.
 *
 * Several voices can hold the same track under different orbiter/world keys, and each read the
 * personal layer at its own moment: one can have been answered and another not, which leaves one
 * voice holding real values and the other holding defaults. A save knows only the track id, so it
 * has to be able to ask about every config the track is held under — a first-match scan would answer
 * for whichever one it happened to hit and permit a write derived from values nobody read.
 *
 * Re-reading a config replaces its own entry, so a voice that recovers stops holding the track back.
 * @type {Map<string, string>} config key -> outcome
 */
const provenanceByConfig = new Map();

/** The two outcomes that mean the server actually spoke. Everything else is an absence of an answer,
 *  not an answer of absence. */
function isAnswered(outcome) {
  return outcome === READ_OUTCOME.NETWORK || outcome === READ_OUTCOME.ABSENT;
}

function provenanceKey(combined, trackId) {
  if (!trackId) return null;
  try {
    return Constants.buildConfigKey({
      trackId,
      orbiterId: combined?.orbiter?.orbiterId || null,
      entangledWorldId: combined?.entangledWorld?.worldId || combined?.track?.entangledWorld?.worldId || null,
    });
  } catch {
    return null;
  }
}

/**
 * File the resolved block this fold just produced under the person it belongs to, beside the
 * downloaded release it belongs with.
 *
 * The per-owner row is otherwise written in exactly ONE place — the download itself. A collection
 * downloaded before the personal layer was lifted out of the shared release row therefore has no
 * row at all: the migration cannot attribute a block baked into a row two accounts were sharing, so
 * it drops the personal half rather than hand it to whoever boots next. Online nothing shows,
 * because this fold rebuilds the answer from a fresh read on every boot. The next boot with NO
 * network has nothing, and the person's tempo, downbeat, meter, warp and loop are gone until they
 * download the whole collection again.
 *
 * Mirroring the fold closes that, and closes every other way the row can go missing — a quota that
 * refused the write at download time, a repair transaction that aborted — because one ordinary
 * online boot puts it back and it stays back.
 *
 * Only where a stored release actually lives. This row is the personal half of a DOWNLOADED
 * release, so writing one for a track that was merely played would file rows for every track
 * anybody ever opened, none of which an offline boot will ever read. That gate also settles the
 * version question on its own: `releaseRecordKey` keys an unpinned release as `version:live`, so if
 * a collection really was downloaded unpinned then that is the key its release is under and the
 * mirror belongs there — while a track played live with no pin has no stored release and gets no
 * row. One rule, rather than a second opinion about what a version means.
 */
async function mirrorFoldedEffectiveSettings({ trackId, version, effectiveSettings, identity }) {
  try {
    const resourceKey = releaseRecordKey({ kind: 'track', id: trackId, version });
    if (!resourceKey || !(await readStoredRelease(resourceKey))) return;
    await mirrorAccepted({
      kind: CONFIGURATION_KINDS.EFFECTIVE_SETTINGS,
      resourceKey,
      value: effectiveSettings,
      identity: await identity,
    });
  } catch {
    // A mirror that could not be written is a boot that still works, on the answer the server just
    // gave. It is for the NEXT boot, and nothing about it may take this one down.
  }
}

/**
 * Put a freshly-read personal layer over the resolved one, and record where it came from.
 *
 * Only an outcome the server actually gave is acted on. A layer served from a mirror, or not had at
 * all, is not newer than the resolved answer and has no business replacing it — the resolved answer
 * is at least an answer the server once gave. An authoritative ABSENT is acted on precisely because
 * it is an answer: it says this person has no settings, which is how a deletion made elsewhere
 * reaches a browser still holding the release it was frozen into.
 *
 * @param {object|null} combined the assembled config
 * @param {{outcome: string, value: object|null}} read what the personal-layer read found
 * @param {string|null} [trackId] the track this read was for, when the combined does not name it
 * @param {Promise<{epoch: number, ownerKey: string|null}|null>|{epoch: number, ownerKey: string|null}|null} [identity]
 *   who this read was issued for, BEGUN BY THE CALLER BEFORE IT ISSUED THE READ — that ordering is
 *   the whole point, since an identity settled afterwards is the one that arrived DURING the
 *   request, which is exactly the account the answer must not be filed under. A promise is accepted
 *   so the caller does not have to await an owner gate on the boot path; the mirror awaits it.
 *   Left out, nothing is mirrored — the fold still applies.
 * @returns {object|null} the combined config, with the fold applied and the outcome attached
 */
export function attachPersonalTrackSync(combined, read, trackId = null, identity = null) {
  if (!combined || typeof combined !== 'object') return combined;
  const outcome = read?.outcome || READ_OUTCOME.UNAVAILABLE;
  const personal = read?.value ?? null;

  const resolvedTrackId = trackId || combined?.track?.trackId || null;
  const key = provenanceKey(combined, resolvedTrackId);
  if (key) provenanceByConfig.set(key, outcome);

  const next = {
    ...combined,
    // On an answer the personal layer IS what the server just said, `null` included — keeping the
    // one frozen into a downloaded release would go on serving settings the person has deleted.
    trackUserSettings: isAnswered(outcome) ? personal : (combined?.trackUserSettings ?? null),
    // The one thing a `null` could never say for itself: which kind of nothing it is. Carried on the
    // config so the commit seam can state it for the voice it is editing.
    trackUserSettingsOutcome: outcome,
  };
  if (!isAnswered(outcome)) return next;

  const resolved = resolvedSyncOf(next);
  if (!resolved) return next;

  const effectiveSettings = {
    ...next.track.metadata.effectiveSettings,
    sync: foldPersonalSyncOverResolved(resolved, personal?.sync),
  };

  // Deliberately not awaited: this boot already has its answer, and the mirror is for the next one.
  // A file on this machine is skipped outright — it has no release row and no settings row anywhere
  // on the platform, so there is nothing for a mirror to stand in for.
  if (identity && resolvedTrackId && !isLocalEntryId(resolvedTrackId)) {
    void mirrorFoldedEffectiveSettings({
      trackId: resolvedTrackId,
      version: next.track?.version ?? null,
      effectiveSettings,
      identity,
    });
  }

  return {
    ...next,
    track: { ...next.track, metadata: { ...next.track.metadata, effectiveSettings } },
  };
}

/**
 * Whether this track's settings may be written wholesale right now.
 *
 * A commit re-sends every field, so it can only be built on a personal layer that was actually
 * read. `absent` counts: the server saying "you have nothing saved" is an answer, and a first save
 * on top of it destroys nothing. Everything else — an unreachable server, a declined identity, a
 * response that would not parse — means the values in memory came from defaults and detection, and
 * sending them would overwrite settings the person still has.
 *
 * A track nothing has read AT ALL is refused, not waved through. There is no legitimate save before
 * the read: the commit seam gets its track id from an engine whose config came through the attach
 * above, so "never read" means a save that got ahead of the boot, and a permissive answer there is
 * how the guard was bypassed entirely.
 *
 * THE LEDGER AUTHORISES, ALWAYS — a stated outcome only narrows it. The stated outcome is a FIELD
 * on a cached config, and nothing clears that field when the person here changes; the ledger is the
 * thing a change of identity clears. Trusted on its own it therefore granted permission on the
 * previous account's read: the config account A booted still says `network`, and account B, editing
 * a tempo a second after the switch, was waved through and wrote a wholesale payload over settings
 * B's session had never read. So the track has to be in the ledger first, which after a switch it
 * is not until B's own read lands.
 *
 * @param {string} trackId
 * @param {string|null} [statedOutcome] the provenance of the config being edited, from the caller
 *   that knows WHICH voice that is. It chooses AMONG the reads this identity has made; it cannot
 *   stand in for one. Left unstated, every config this track is held under has to agree, because
 *   the guard cannot tell which one the values came from.
 * @returns {boolean}
 */
export function mayCommitWholesaleSync(trackId, statedOutcome = null) {
  // A file on this machine has no settings row on the platform: nothing can be read, and nothing
  // can be destroyed by a save that never leaves the session.
  if (isLocalEntryId(trackId)) return true;
  if (!trackId || typeof trackId !== 'string') return false;

  const prefix = `${trackId}|`;
  let known = false;
  let allAnswered = true;
  for (const [key, outcome] of provenanceByConfig) {
    if (key !== trackId && !key.startsWith(prefix)) continue;
    known = true;
    if (!isAnswered(outcome)) allAnswered = false;
  }
  if (!known) return false;
  return statedOutcome ? isAnswered(statedOutcome) : allAnswered;
}

/**
 * Forget what this page learned on behalf of the person who was here.
 *
 * Called at both identity edges, beside the other per-device invalidations. Provenance is an answer
 * about ONE account's settings — kept across a change of identity it would tell the arriving person
 * that their settings were read when what was read was somebody else's.
 */
export function resetTrackUserSettingsProvenance() {
  provenanceByConfig.clear();
}
